import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Users, FolderOpen, BookOpen, FileText, HardHat, Receipt,
  LogOut, ChevronLeft, Ruler, RefreshCw, Brain, Settings, Wallet, Truck, Archive, Inbox,
} from 'lucide-react';
import API from '../api';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'lead', label: 'Lead / Richieste', icon: Inbox },
  { id: 'cantieri', label: 'Cantieri', icon: HardHat },
  { id: 'computi', label: 'Computi Metrici', icon: Ruler },
  { id: 'preventivi', label: 'Preventivi', icon: FileText },
  { id: 'conti', label: 'Conti', icon: Receipt },
  { id: 'pratiche', label: 'Pratiche', icon: FolderOpen },
  { id: 'clienti', label: 'Clienti', icon: Users },
  { id: 'fornitori', label: 'Fornitori', icon: Truck },
  { id: 'contabilita', label: 'Contabilita', icon: Wallet },
  { id: 'prezzario', label: 'Prezzario', icon: BookOpen },
  { id: 'listino', label: 'Listino Aziendale', icon: RefreshCw },
  { id: 'archivio', label: 'Archivio', icon: Archive },
  { id: 'formazione', label: 'Formazione AI', icon: Brain },
];

const RUOLO_LABELS = {
  admin: 'Amministratore', impiegato: 'Impiegato', operatore: 'Operatore',
  ufficio: 'Impiegato', tecnico: 'Impiegato', capocantiere: 'Operatore', collaboratore: 'Operatore',
};

function isAdmin(user) {
  return user?.ruolo === 'admin' || user?.email === 'admin@edilcima.it';
}

function NavBadge({ count, sidebarOpen }) {
  if (!count) return null;
  if (sidebarOpen) {
    return (
      <span className="bg-red-500 text-white text-[10px] font-black rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="notif-badge">
        {count}
      </span>
    );
  }
  return (
    <span className="absolute right-1 top-1 bg-red-500 text-white text-[8px] font-black rounded-full w-4 h-4 flex items-center justify-center">
      {count > 9 ? '9+' : count}
    </span>
  );
}

export default function Sidebar({ user, page, sidebarOpen, mobileSidebar, notifCount, onNavigate, onToggle, onLogout, onCloseMobile }) {
  const items = isAdmin(user)
    ? [...NAV_ITEMS, { id: 'admin', label: 'Amministrazione', icon: Settings }]
    : NAV_ITEMS;

  const [newLeads, setNewLeads] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await API.get('/api/lead', { params: { stato: 'nuovo' } });
        if (!cancelled) setNewLeads(data.by_stato?.nuovo || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000); // refresh every 60s
    return () => { cancelled = true; clearInterval(t); };
  }, [page]);

  return (
    <>
      {mobileSidebar && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={onCloseMobile} />
      )}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 lg:z-auto bg-slate-950 text-slate-50 flex flex-col transition-[width,transform] duration-300 border-r border-slate-800 ${sidebarOpen ? 'w-64' : 'w-16'} ${mobileSidebar ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        <div className="p-4 flex items-center gap-3 border-b border-slate-800">
          <img src="/edilcima_logo.png" alt="EDIL CIMA SRL" className="w-10 h-10 rounded object-contain flex-shrink-0 bg-slate-900 p-0.5" />
          {sidebarOpen && (
            <div className="leading-tight">
              <div className="font-black text-base tracking-tight text-white">EDIL CIMA</div>
              <div className="text-[10px] text-slate-400 tracking-widest">S.R.L.</div>
            </div>
          )}
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {items.map(item => (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              data-testid={`nav-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-all relative ${page === item.id ? 'bg-primary text-primary-foreground border-r-[3px] border-primary shadow-inner' : 'text-slate-100 hover:bg-slate-800 hover:text-white'}`}
            >
              <item.icon size={20} className={`flex-shrink-0 ${page === item.id ? 'text-primary-foreground' : 'text-slate-200'}`} />
              {sidebarOpen && <span className="flex-1 text-left">{item.label}</span>}
              {item.id === 'contabilita' && <NavBadge count={notifCount} sidebarOpen={sidebarOpen} />}
              {item.id === 'lead' && <NavBadge count={newLeads} sidebarOpen={sidebarOpen} />}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800">
          {sidebarOpen && (
            <div className="mb-3 px-2">
              <p className="text-xs font-bold text-white truncate">{user.nome} {user.cognome}</p>
              <p className="text-xs text-slate-400">{RUOLO_LABELS[user.ruolo] || user.ruolo}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button onClick={onToggle} className="p-2 hover:bg-slate-800 rounded-md transition-colors hidden lg:block text-slate-200 hover:text-white" data-testid="toggle-sidebar">
              <ChevronLeft size={16} className={`transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
            </button>
            <button onClick={onLogout} className="p-2 hover:bg-slate-800 rounded-md transition-colors flex-1 flex items-center gap-2 text-sm text-slate-200 hover:text-white" data-testid="logout-btn">
              <LogOut size={16} />
              {sidebarOpen && <span>Esci</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
